import { Button } from '@nexdel/ui/components/button';
import { useMemo, useState } from 'react';
import { ClipboardList, Clock, CheckCircle2 } from '@nexdel/core/utils/icons';
import { api } from '@nexdel/core/api';
import { StarRating } from '@nexdel/ui/components/star-rating';
import { BrandLogo } from '@nexdel/ui/layout/BrandLogo';

/**
 * Common context shape for both the external (token-gated) and internal
 * (auth-gated) feedback flows. Each flow's GET endpoint resolves to this shape.
 */
export interface FeedbackContext {
  participantName: string;
  candidateName: string;
  requirementTitle: string;
  mandateName: string;
  interviewType: string | null;
  scheduledAt: string | null;
  alreadySubmitted: boolean;
  submittedFeedback: {
    overallRating: number;
    recommendation: 'hire' | 'no_hire' | 'maybe';
    technicalRating: number | null;
    softSkillsRating: number | null;
    technicalNotes: string | null;
    softSkillsNotes: string | null;
    strengths: string | null;
    concerns: string | null;
  } | null;
}

type Recommendation = 'hire' | 'no_hire' | 'maybe';

type T = (k: string, opts?: Record<string, unknown>) => string;

// ── Page shell (branded standalone layout) ──────────────────────────────────

export function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-muted/30 flex flex-col">
      <header className="bg-primary text-primary-foreground py-4 px-6">
        <BrandLogo className="h-16 w-auto" />
      </header>
      <main className="flex-1 flex items-start justify-center py-10 px-4">
        <div className="w-full max-w-2xl bg-card border border-border rounded-lg shadow-sm p-6 space-y-5">
          {children}
        </div>
      </main>
      <footer className="text-center py-4 text-xs text-muted-foreground">
        Nexdel — Supplier Management Platform
      </footer>
    </div>
  );
}

// ── Context card ────────────────────────────────────────────────────────────

export function ContextCard({ ctx, fmtDateTime, label }: {
  ctx: FeedbackContext;
  fmtDateTime: (iso: string | null) => string;
  label: string;
}) {
  return (
    <div className="rounded-md border border-border bg-muted/40 p-4 space-y-1.5">
      <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
        {label}
      </p>
      <p className="text-sm font-semibold text-foreground">{ctx.candidateName}</p>
      <p className="text-xs text-muted-foreground">{ctx.requirementTitle} · {ctx.mandateName}</p>
      {ctx.interviewType && (
        <p className="text-xs text-muted-foreground">{ctx.interviewType}</p>
      )}
      {ctx.scheduledAt && (
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Clock size={11} /> {fmtDateTime(ctx.scheduledAt)}
        </p>
      )}
    </div>
  );
}

// ── Submitted-feedback read-only card ───────────────────────────────────────

export function SubmittedFeedbackCard({ f, t }: {
  f: NonNullable<FeedbackContext['submittedFeedback']>;
  t: T;
}) {
  return (
    <div className="rounded-md border border-border p-4 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
          {t('interviews.yourFeedback')}
        </span>
        <div className="flex items-center gap-2">
          <StarRating value={f.overallRating} readOnly size="sm" />
          <span className="text-xs font-medium">{t(`interviews.recommendations.${f.recommendation}`)}</span>
        </div>
      </div>
      {(f.technicalRating != null || f.technicalNotes) && (
        <div>
          <div className="flex items-center gap-2">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('interviews.technicalSkills')}</p>
            {f.technicalRating != null && <StarRating value={f.technicalRating} readOnly size="sm" />}
          </div>
          {f.technicalNotes && <p className="text-xs text-foreground whitespace-pre-wrap mt-0.5">{f.technicalNotes}</p>}
        </div>
      )}
      {(f.softSkillsRating != null || f.softSkillsNotes) && (
        <div>
          <div className="flex items-center gap-2">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('interviews.softSkills')}</p>
            {f.softSkillsRating != null && <StarRating value={f.softSkillsRating} readOnly size="sm" />}
          </div>
          {f.softSkillsNotes && <p className="text-xs text-foreground whitespace-pre-wrap mt-0.5">{f.softSkillsNotes}</p>}
        </div>
      )}
      {f.strengths && (
        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('interviews.strengths')}</p>
          <p className="text-xs text-foreground whitespace-pre-wrap">{f.strengths}</p>
        </div>
      )}
      {f.concerns && (
        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('interviews.concerns')}</p>
          <p className="text-xs text-foreground whitespace-pre-wrap">{f.concerns}</p>
        </div>
      )}
    </div>
  );
}

// ── Form ────────────────────────────────────────────────────────────────────

export function FeedbackForm({ ctx, submitUrl, method, fmtDateTime, onSubmitted, t, title, intro, contextLabel, submitButton, footerHint }: {
  ctx: FeedbackContext;
  /** PUT/POST URL — the same body shape works for both the external public endpoint
   *  and the internal authenticated one. The api wrapper attaches the Firebase
   *  token automatically when present, and the backend allows it without one
   *  for public endpoints. */
  submitUrl: string;
  /** HTTP verb to use. External endpoint is POST (token-consume); internal is PUT (upsert). */
  method: 'POST' | 'PUT';
  fmtDateTime: (iso: string | null) => string;
  onSubmitted: () => void;
  t: T;
  /** Header copy — varies between the two flows. */
  title: string;
  intro: string;
  contextLabel: string;
  submitButton: string;
  footerHint?: string;
}) {
  const initial = ctx.submittedFeedback;
  const [rating, setRating] = useState<number>(initial?.overallRating ?? 0);
  const [recommendation, setRecommendation] = useState<Recommendation | ''>(
    initial?.recommendation ?? '',
  );
  const [technicalRating, setTechnicalRating] = useState<number>(initial?.technicalRating ?? 0);
  const [softSkillsRating, setSoftSkillsRating] = useState<number>(initial?.softSkillsRating ?? 0);
  const [technicalNotes, setTechnicalNotes] = useState(initial?.technicalNotes ?? '');
  const [softSkillsNotes, setSoftSkillsNotes] = useState(initial?.softSkillsNotes ?? '');
  const [strengths, setStrengths] = useState(initial?.strengths ?? '');
  const [concerns, setConcerns] = useState(initial?.concerns ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const canSubmit = useMemo(
    () => rating >= 1 && rating <= 5 && recommendation !== '' && !submitting,
    [rating, recommendation, submitting],
  );

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const body = {
        overallRating: rating,
        recommendation,
        technicalRating: technicalRating >= 1 ? technicalRating : null,
        softSkillsRating: softSkillsRating >= 1 ? softSkillsRating : null,
        technicalNotes: technicalNotes.trim() || null,
        softSkillsNotes: softSkillsNotes.trim() || null,
        strengths: strengths.trim() || null,
        concerns: concerns.trim() || null,
      };
      if (method === 'POST') {
        await api.post(submitUrl, body);
      } else {
        await api.put(submitUrl, body);
      }
      onSubmitted();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to submit feedback');
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <ClipboardList size={18} className="text-primary" />
          {title}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {intro.replace('{{name}}', ctx.participantName)}
        </p>
      </div>

      <ContextCard ctx={ctx} fmtDateTime={fmtDateTime} label={contextLabel} />

      <div>
        <label className="block text-xs font-medium text-foreground mb-1.5">
          {t('interviews.overallRating')}
        </label>
        <StarRating value={rating} onChange={setRating} />
      </div>

      <div>
        <label className="block text-xs font-medium text-foreground mb-1.5">
          {t('interviews.recommendation')}
        </label>
        <div className="flex gap-2">
          {(['hire', 'maybe', 'no_hire'] as const).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRecommendation(r)}
              className={`flex-1 py-1.5 text-xs rounded-md border transition ${
                recommendation === r
                  ? r === 'hire'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-400'
                    : r === 'no_hire'
                    ? 'bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400'
                    : 'bg-primary/10 border-primary/30 text-primary'
                  : 'border-border text-muted-foreground hover:bg-accent'
              }`}
            >
              {t(`interviews.recommendations.${r}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3 rounded-md border border-border p-3">
        <div className="flex items-center justify-between gap-3">
          <label className="text-xs font-medium text-foreground">{t('interviews.technicalSkills')}</label>
          <StarRating value={technicalRating} onChange={setTechnicalRating} size="sm" />
        </div>
        <textarea
          value={technicalNotes}
          onChange={(e) => setTechnicalNotes(e.target.value)}
          rows={4}
          placeholder={t('interviews.technicalNotesPlaceholder')}
          className="w-full text-xs rounded-md border border-input bg-background px-2 py-1.5 placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        />
      </div>

      <div className="space-y-3 rounded-md border border-border p-3">
        <div className="flex items-center justify-between gap-3">
          <label className="text-xs font-medium text-foreground">{t('interviews.softSkills')}</label>
          <StarRating value={softSkillsRating} onChange={setSoftSkillsRating} size="sm" />
        </div>
        <textarea
          value={softSkillsNotes}
          onChange={(e) => setSoftSkillsNotes(e.target.value)}
          rows={4}
          placeholder={t('interviews.softSkillsNotesPlaceholder')}
          className="w-full text-xs rounded-md border border-input bg-background px-2 py-1.5 placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-foreground mb-1.5">
          {t('interviews.strengths')}
        </label>
        <textarea
          value={strengths}
          onChange={(e) => setStrengths(e.target.value)}
          rows={5}
          className="w-full text-xs rounded-md border border-input bg-background px-2 py-1.5 placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-foreground mb-1.5">
          {t('interviews.concerns')}
        </label>
        <textarea
          value={concerns}
          onChange={(e) => setConcerns(e.target.value)}
          rows={5}
          className="w-full text-xs rounded-md border border-input bg-background px-2 py-1.5 placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        />
      </div>

      {errorMessage && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {errorMessage}
        </div>
      )}

      <div className="flex justify-end pt-1">
        <Button
          variant="default"
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="px-4 py-2 text-sm rounded-md disabled:opacity-50"
        >
          {submitButton}
        </Button>
      </div>

      {footerHint && (
        <p className="text-[11px] text-muted-foreground text-center">
          {footerHint}
        </p>
      )}
    </div>
  );
}

// ── Thanks state ────────────────────────────────────────────────────────────

export function ThanksState({ ctx, title, body }: {
  ctx: FeedbackContext;
  title: string;
  body: string;
}) {
  return (
    <div className="text-center space-y-3 py-6">
      <CheckCircle2 className="mx-auto text-emerald-600" size={36} />
      <h1 className="text-lg font-semibold text-foreground">{title}</h1>
      <p className="text-sm text-muted-foreground">
        {body.replace('{{candidate}}', ctx.candidateName)}
      </p>
    </div>
  );
}
